import { useState, useEffect } from 'react';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Button, Typography, IconButton, Stack, Slide, Fade } from '@mui/material';
// components
import Iconify from './Iconify';
import Logo from './Logo';
import useAuth from '../hooks/useAuth';

// ----------------------------------------------------------------------

const RootStyle = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0, // Sit tight at the top
    left: 0,
    right: 0,
    zIndex: 11000,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    padding: theme.spacing(1, 0),
}));

const PromptCard = styled(Box)(({ theme }) => ({
    // Default width for desktop/larger screens
    width: 'calc(100% - 16px)',
    maxWidth: 380,
    background: `linear-gradient(135deg, ${theme.palette.primary.darker} 0%, ${theme.palette.primary.main} 100%)`,
    color: '#fff',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.customShadows.z24,
    padding: theme.spacing(0.75, 1.5),
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
    pointerEvents: 'auto',
    // Mobile Adjustment: Make it narrower to fit between icons
    [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 140px)', // ~70px clearance on each side
        maxWidth: '100%',
        padding: theme.spacing(0.5, 1), // Tighter padding on mobile
    },
}));

// ----------------------------------------------------------------------

export default function InstallPrompt() {
    const { setting } = useAuth();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const appName = setting?.general?.app_name || 'Our App';

    useEffect(() => {
        const handler = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e);
            // Update UI notify the user they can install the PWA
            setShowPrompt(true);
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Force show prompt if store URLs are available, even if PWA event doesn't fire (e.g. iOS)
        if (setting?.setting?.playstore_url || setting?.setting?.appstore_url) {
            const hasSeenPrompt = sessionStorage.getItem('installPromptSeen');
            if (!hasSeenPrompt) {
                setShowPrompt(true);
            }
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [setting?.setting?.playstore_url, setting?.setting?.appstore_url]);

    const handleInstallClick = () => {
        // OS Detection
        const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
        const isAndroid = /android/i.test(userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

        // Redirect to Stores if URLs are available
        if (isAndroid && setting?.setting?.playstore_url) {
            window.open(setting.setting.playstore_url, '_blank');
            return;
        }
        if (isIOS && setting?.setting?.appstore_url) {
            window.open(setting.setting.appstore_url, '_blank');
            return;
        }

        // Fallback to PWA prompt if available
        if (!deferredPrompt) return;

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        deferredPrompt.userChoice.then((choiceResult) => {
            if (choiceResult.outcome === 'accepted') {
                console.log('User accepted the install prompt');
            } else {
                console.log('User dismissed the install prompt');
            }
            setDeferredPrompt(null);
            setShowPrompt(false);
        });
    };

    const handleClose = () => {
        setShowPrompt(false);
        sessionStorage.setItem('installPromptSeen', 'true');
    };

    if (!showPrompt) return null;

    return (
        <Fade in={showPrompt}>
            <RootStyle>
                <Slide direction="down" in={showPrompt} mountOnEnter unmountOnExit>
                    <PromptCard>
                        <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 1.5 }} sx={{ flexGrow: 1, minWidth: 0 }}>
                            {/* Logo */}
                            <Box sx={{
                                flexShrink: 0,
                                bgcolor: 'white',
                                borderRadius: '50%',
                                width: { xs: 24, md: 32 }, // Smaller logo on mobile
                                height: { xs: 24, md: 32 },
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px solid white',
                                display: 'flex'
                            }}>
                                <Logo disabledLink sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </Box>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', noWrap: true, display: 'block', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                                    Install {appName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'grey.400', display: { xs: 'none', md: 'block' }, fontSize: '0.65rem' }}>
                                    Buy Data, Airtime & more...
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleInstallClick}
                                sx={{
                                    bgcolor: 'common.white',
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    px: 1, // Tighter button padding
                                    py: 0.2,
                                    fontSize: '0.7rem',
                                    minWidth: 'auto',
                                    '&:hover': { bgcolor: 'grey.100' }
                                }}
                            >
                                Install
                            </Button>

                            <IconButton
                                size="small"
                                onClick={handleClose}
                                sx={{ color: 'common.white', opacity: 0.6, p: 0.2, '&:hover': { opacity: 1 } }}
                            >
                                <Iconify icon={'eva:close-fill'} width={16} height={16} />
                            </IconButton>
                        </Stack>
                    </PromptCard>
                </Slide>
            </RootStyle>
        </Fade>
    );
}
